import { supabase } from '../../../integrations/supabase/client';

// --- WAHA Configuration (from env vars) ---
const WAHA_BASE_URL = import.meta.env.VITE_WAHA_BASE_URL || '';
const WAHA_API_KEY = import.meta.env.VITE_WAHA_API_KEY || '';

const wahaHeaders: Record<string, string> = {
  'Content-Type': 'application/json',
  'X-Api-Key': WAHA_API_KEY,
};

// --- Types ---
export interface WhatsAppConnection {
  id: string;
  user_id: string;
  nombre_instancia: string;
  numero_telefono: string;
  api_key: string | null;
  qr_code_base64: string | null;
  estado: 'pending' | 'connected' | 'disconnected' | 'error';
  created_at: string;
  updated_at: string;
  connected_at: string | null;
  last_activity: string | null;
}

export type WahaSessionStatus = 'STOPPED' | 'STARTING' | 'SCAN_QR_CODE' | 'WORKING' | 'FAILED';

export interface WahaSessionInfo {
  name: string;
  status: WahaSessionStatus;
  me?: {
    id: string;
    pushName: string;
  } | null;
}

export interface QRPollResult {
  status: WahaSessionStatus;
  qrBase64?: string;
  phoneNumber?: string;
}

// --- Service ---
export const whatsappService = {
  // Read Contador_1 and return next instance name (maxi-{N+1})
  async getNextInstanceName(): Promise<string> {
    const { data, error } = await (supabase as any)
      .from('Contador_1')
      .select('contador')
      .eq('id', 3)
      .maybeSingle();

    if (error) {
      console.error('[WhatsApp] Error leyendo Contador_1:', error);
      throw new Error('Error al obtener el contador de instancias');
    }
    if (!data || data.contador == null) {
      throw new Error('No se encontró el registro del contador (id=3)');
    }

    const nextNumber = Number(data.contador) + 1;
    return `maxi-${nextNumber}`;
  },

  // Increment counter after successful connection
  async incrementCounter(): Promise<void> {
    const { data, error: readError } = await (supabase as any)
      .from('Contador_1')
      .select('contador')
      .eq('id', 3)
      .maybeSingle();

    if (readError || !data || data.contador == null) {
      console.error('[WhatsApp] Error leyendo contador para incrementar:', readError);
      return; // Non-critical, don't throw
    }

    const { error: updateError } = await (supabase as any)
      .from('Contador_1')
      .update({ contador: Number(data.contador) + 1 })
      .eq('id', 3);

    if (updateError) {
      console.error('[WhatsApp] Error incrementando contador:', updateError);
    }
  },

  // Create a new WAHA session
  async createSession(sessionName: string): Promise<{ sessionName: string; status: WahaSessionStatus }> {
    const response = await fetch(`${WAHA_BASE_URL}/api/sessions/`, {
      method: 'POST',
      headers: wahaHeaders,
      body: JSON.stringify({
        name: sessionName,
        config: {},
      }),
    });

    if (!response.ok) {
      const errBody = await response.text();
      throw new Error(`Error al crear sesión WAHA: ${response.status} - ${errBody}`);
    }

    const data = await response.json();
    return {
      sessionName: data.name || sessionName,
      status: data.status || 'STOPPED',
    };
  },

  // Start a stopped WAHA session
  async startSession(sessionName: string): Promise<void> {
    const response = await fetch(`${WAHA_BASE_URL}/api/sessions/${sessionName}/start`, {
      method: 'POST',
      headers: wahaHeaders,
    });

    if (!response.ok) {
      throw new Error(`Error al iniciar sesión: ${response.status}`);
    }
  },

  // Get session info
  async getSessionInfo(sessionName: string): Promise<WahaSessionInfo> {
    const response = await fetch(`${WAHA_BASE_URL}/api/sessions/${sessionName}`, {
      method: 'GET',
      headers: wahaHeaders,
    });

    if (!response.ok) {
      throw new Error(`Error al obtener info de sesión: ${response.status}`);
    }

    return await response.json();
  },

  // Get QR code as base64 data URI
  async getQRCode(sessionName: string): Promise<string | null> {
    try {
      const response = await fetch(
        `${WAHA_BASE_URL}/api/${sessionName}/auth/qr?format=image`,
        {
          method: 'GET',
          headers: {
            ...wahaHeaders,
            'Accept': 'application/json',
          },
        }
      );

      if (!response.ok) {
        return null;
      }

      const data = await response.json();
      return `data:${data.mimetype};base64,${data.data}`;
    } catch {
      return null;
    }
  },

  // Combined poll: check status + get QR if needed
  async pollSessionStatus(sessionName: string): Promise<QRPollResult> {
    const info = await this.getSessionInfo(sessionName);

    if (info.status === 'WORKING') {
      const phoneNumber = info.me?.id
        ? info.me.id.replace('@c.us', '')
        : '';
      return { status: 'WORKING', phoneNumber };
    }

    if (info.status === 'SCAN_QR_CODE') {
      const qrBase64 = await this.getQRCode(sessionName);
      return { status: 'SCAN_QR_CODE', qrBase64: qrBase64 || undefined };
    }

    // Try to get QR even in STARTING state — WAHA sometimes has it ready
    if (info.status === 'STARTING') {
      const qrBase64 = await this.getQRCode(sessionName);
      if (qrBase64) {
        return { status: 'SCAN_QR_CODE', qrBase64 };
      }
    }

    return { status: info.status };
  },

  // Restart a WAHA session
  async restartSession(sessionName: string): Promise<void> {
    const response = await fetch(
      `${WAHA_BASE_URL}/api/sessions/${sessionName}/restart`,
      { method: 'POST', headers: wahaHeaders }
    );

    if (!response.ok) {
      throw new Error(`Error al reiniciar sesión: ${response.status}`);
    }
  },

  // Logout a WAHA session (requires re-scanning QR)
  async logoutSession(sessionName: string): Promise<void> {
    await fetch(
      `${WAHA_BASE_URL}/api/sessions/${sessionName}/logout`,
      { method: 'POST', headers: wahaHeaders }
    );
  },

  // Stop a WAHA session
  async stopSession(sessionName: string): Promise<void> {
    await fetch(
      `${WAHA_BASE_URL}/api/sessions/${sessionName}/stop`,
      { method: 'POST', headers: wahaHeaders }
    );
  },

  // Delete a WAHA session completely
  async deleteWahaSession(sessionName: string): Promise<void> {
    await fetch(
      `${WAHA_BASE_URL}/api/sessions/${sessionName}`,
      { method: 'DELETE', headers: wahaHeaders }
    );
  },

  // --- Supabase CRUD (kept from original) ---

  async saveConnection(connectionData: {
    user_id: string;
    nombre_instancia: string;
    numero_telefono: string;
    api_key: string;
    qr_code_base64: string;
    estado: 'pending' | 'connected' | 'disconnected' | 'error';
  }): Promise<WhatsAppConnection> {
    const { data, error } = await (supabase as any)
      .from('whatsapp_connections')
      .insert(connectionData)
      .select()
      .single();

    if (error) throw error;
    return data as WhatsAppConnection;
  },

  async updateConnectionStatus(
    id: string,
    estado: 'pending' | 'connected' | 'disconnected' | 'error',
    additionalData?: { connected_at?: string; qr_code_base64?: string; numero_telefono?: string }
  ): Promise<void> {
    const updateData: Record<string, unknown> = {
      estado,
      updated_at: new Date().toISOString(),
    };

    if (additionalData?.connected_at) {
      updateData.connected_at = additionalData.connected_at;
    }
    if (additionalData?.qr_code_base64) {
      updateData.qr_code_base64 = additionalData.qr_code_base64;
    }
    if (additionalData?.numero_telefono) {
      updateData.numero_telefono = additionalData.numero_telefono;
    }

    const { error } = await (supabase as any)
      .from('whatsapp_connections')
      .update(updateData)
      .eq('id', id);

    if (error) throw error;
  },

  async getConnections(userId: string): Promise<WhatsAppConnection[]> {
    const { data, error } = await (supabase as any)
      .from('whatsapp_connections')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return (data || []) as WhatsAppConnection[];
  },

  async deleteConnection(id: string, instanceName: string): Promise<void> {
    console.log('[WhatsApp] Iniciando eliminación:', { id, instanceName });

    // Step 1: Clean up WAHA session (best-effort)
    try {
      await this.logoutSession(instanceName);
      await this.stopSession(instanceName);
      await this.deleteWahaSession(instanceName);
      console.log('[WhatsApp] Sesión WAHA eliminada');
    } catch (error) {
      console.error('[WhatsApp] Error limpiando sesión WAHA (continuando):', error);
    }

    // Step 2: Delete from database (CRITICAL)
    console.log('[WhatsApp] Eliminando de BD con id:', id);

    const { error, data } = await supabase
      .from('whatsapp_connections')
      .delete()
      .eq('id', id)
      .select();

    if (error) {
      console.error('[WhatsApp] Error eliminando de BD:', error);
      throw new Error(`Error al eliminar de base de datos: ${error.message}`);
    }

    console.log('[WhatsApp] Registro eliminado de BD, filas afectadas:', data?.length || 0);
  },
};
